import { Banner, Button, Flex, Spinner, Text } from '@chatsdet/ui';
import { motion } from 'framer-motion';

type FlowListImportBannerProps = {
  importStream: {
    status: 'idle' | 'connecting' | 'streaming' | 'completed' | 'error';
    failedRows: number;
    autoRunCount: number;
    totalRows: number;
    processedRows: number;
    completedRows: number;
  };
  onViewErrors: () => void;
  onDismiss?: () => void;
};

function getBannerMessage(importStream: FlowListImportBannerProps['importStream']) {
  if (importStream.status === 'completed') {
    if (importStream.autoRunCount > 0) {
      if (importStream.failedRows > 0) {
        return `Import complete with ${importStream.failedRows} failed — running ${importStream.autoRunCount} test${importStream.autoRunCount === 1 ? '' : 's'}...`;
      }

      return `Import complete — running ${importStream.autoRunCount} test${importStream.autoRunCount === 1 ? '' : 's'}...`;
    }

    if (importStream.failedRows > 0) {
      return `Import complete with ${importStream.failedRows} failed`;
    }

    return 'Import complete';
  }

  if (importStream.status === 'connecting') {
    return 'Connecting...';
  }

  if (importStream.status === 'error') {
    return 'Import stream disconnected';
  }

  if (importStream.totalRows === 0) {
    return 'Analyzing upload...';
  }

  return `AI building workflows... ${importStream.processedRows}/${importStream.totalRows} processed`;
}

export function FlowListImportBanner({
  importStream,
  onViewErrors,
  onDismiss,
}: FlowListImportBannerProps) {
  return (
    <Banner
      variant={
        importStream.status === 'completed'
          ? importStream.failedRows > 0
            ? 'warning'
            : 'success'
          : importStream.status === 'error'
            ? 'warning'
          : 'info'
      }
      className="mb-4"
    >
      <div className="flex-1 min-w-0">
        <Flex align="center" gap="sm">
          {importStream.status !== 'completed' && (
            <motion.div
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Spinner size="sm" />
            </motion.div>
          )}
          <Text size="sm" weight="medium">
            {getBannerMessage(importStream)}
          </Text>
          {importStream.failedRows > 0 && (
            <Button
              variant="link"
              size="sm"
              className="underline"
              onPress={onViewErrors}
            >
              View {importStream.failedRows} error{importStream.failedRows === 1 ? '' : 's'}
            </Button>
          )}
        </Flex>
        {importStream.totalRows > 0 && (
          <div className="mt-2 h-1.5 w-full bg-blue-200/50 dark:bg-blue-800/30 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-blue-500 dark:bg-blue-400 rounded-full"
              initial={{ width: 0 }}
              animate={{
                width: `${(importStream.processedRows / importStream.totalRows) * 100}%`,
              }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          </div>
        )}
      </div>
      {importStream.completedRows > 0 && (
        <Text size="xs" className="text-muted-foreground shrink-0">
          {importStream.completedRows} workflow{importStream.completedRows === 1 ? '' : 's'} created
        </Text>
      )}
      {importStream.status === 'completed' && onDismiss && (
        <Button variant="ghost" size="sm" onPress={onDismiss} aria-label="Dismiss">
          ✕
        </Button>
      )}
    </Banner>
  );
}
